import React, { useEffect, useRef, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import moment from "moment";

import {
  alpha,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Collapse,
  Divider,
  Fab,
  Grid,
  ImageList,
  List,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
  Zoom
} from "@mui/material";

import ArticleFeedback from "./article-feedback";
import LightBoxModal from "../light-box/light-box-modal";

import {
  ArrowForwardIos,
  AttachFile,
  ExpandLess,
  ExpandMore,
  KeyboardArrowUp,
  Search,
  AccessTime
} from "@mui/icons-material";
import CustomIcon from "../custom-icon";
import { styled } from "@mui/styles";

const FilterStyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "customcolor",
})(({ customcolor }) => ({
  backgroundColor: `${alpha(customcolor, 0.1)} !important`, // 20 = 12% opacity no hex
  borderColor: `${customcolor} !important`,
  color: `${customcolor} !important`,
  fontWeight: 500,

  cursor: "pointer",

  // Ícone de delete (x)
  "& .MuiChip-deleteIcon": {
    color: `${customcolor} !important`,
    "&:hover": {
      color: `${customcolor} !important`,
      opacity: 0.7,
    },
  },

  "&:hover": {
    backgroundColor: `${alpha(customcolor, 0.3)} !important`,
  },
}));


export default function ArticleMainContent({
  state,
  classes,
  classes2,
  clientportal,

  handleBreadCrumbClick,
  handleClickOpenSectionBreadCrumbs,
  handleGoToNewArticle,
  downloadFile,
  handleTagClick,

  handleInitialFeedback,
  resetFeedbackState,
  handleChange,
  handleFeedbackSubmit,

  goToScrollTop,
  topRef,
}) {
  const contentRef = useRef(null);
  const intl = useIntl();

  const [showScrollTop, setShowScrollTop] = useState(false);

  const hasArticleSectionsExternalBreadCrumb =
    state.articleSectionsExternalBreadCrumb
    && state.articleSectionsExternalBreadCrumb.length > 0
    && !state.loading;

  const article = state.article || {};

  const defaultClientColor = clientportal?.defaultcolor ? clientportal?.defaultcolor : "";

  function renderSectionsBreadCrumb() {
    const { articlesections } = state;

    const actualPage = {
      ...article,
      isActualPage: true,
      id: article.title,
      name: article.title,
    };

    const _articlesections = [...articlesections, actualPage];

    const isOnlyActualPage = _articlesections.every(item => item.isActualPage === true);

    return (
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={
          <CustomIcon
            icon={ArrowForwardIos}
            color={defaultClientColor}
            iconSx={{ fontSize: "medium" }}
          />}
      >
        {_articlesections.map((section, _) => {
          const isActive = section?.isActualPage || isOnlyActualPage;

          const isActiveProps = isActive
            ? { sx: { color: defaultClientColor, fontWeight: 600 } }
            : {
              onClick: () => handleBreadCrumbClick(section.id),
              sx: {
                cursor: "pointer",
                color: isOnlyActualPage && defaultClientColor ? defaultClientColor : null,
                "&:hover": {
                  textDecoration: "underline",
                }
              }
            };

          return (
            <Typography
              key={section.id}
              variant="h5"
              {...isActiveProps}
            >
              {section.name}
            </Typography>
          );
        })}
      </Breadcrumbs>
    );
  }

  function renderArticlesCollapseToSection(props) {
    const { article, clientportal } = state;

    const defaultClientColor = clientportal.defaultcolor ? clientportal.defaultcolor : null;

    const { id, title } = props;

    const currentArticleId = article.id;

    const isActive = id === currentArticleId;

    const isActiveProps = isActive
      ? { style: { cursor: "default" } }
      : { onClick: () => handleGoToNewArticle(id) };

    return (
      <li>
        <ListItemButton
          {...isActiveProps}
          sx={{
            pl: 4,
          }}>
          <Tooltip title={title} placement="right" arrow>
            <Typography
              variant="h6"
              children={title}
              sx={{
                color: isActive && defaultClientColor ? defaultClientColor : "primary",
                fontWeight: isActive ? 600 : 300,
                width: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            />
          </Tooltip>
        </ListItemButton>
      </li>
    );
  }

  function renderLabelSectionsAndArticles(props) {
    const { id, open, mapName, mapArticles } = props;

    const hasMapArticles = mapArticles && mapArticles.length > 0;

    let icon = null;
    if (open != null && hasMapArticles) {
      icon = open
        ? <CustomIcon icon={ExpandLess} color={defaultClientColor} />
        : <CustomIcon icon={ExpandMore} color={defaultClientColor} />;
    }

    if (hasMapArticles) {
      return (
        <>
          <li style={{ width: '100%' }}>
            <ListItemButton
              sx={{
                justifyContent: 'space-between',
                width: '100%'
              }}
              onClick={() => handleClickOpenSectionBreadCrumbs(id)}
            >
              <Tooltip title={mapName} placement="top" arrow>
                <Typography variant="h6" children={mapName}
                  sx={{
                    width: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }} />
              </Tooltip>

              {icon}
            </ListItemButton>
          </li>

          <Collapse component="li" in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {mapArticles.map((article) => renderArticlesCollapseToSection(article))}
            </List>
          </Collapse>
        </>
      )
    }

    return (
      <li style={{ width: '100%' }}>
        <ListItemButton
          sx={{
            justifyContent: 'space-between',
            width: '100%'
          }}
          onClick={() => handleBreadCrumbClick(id)}
        >
          <Tooltip title={mapName} placement="top" arrow>
            <Typography
              variant="h6"
              children={mapName}
              sx={{
                width: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            />
          </Tooltip>

          {icon}
        </ListItemButton>
      </li>
    );
  }

  function renderSectionsAndArticlesBreadCrumb() {
    const { articleSectionsExternalBreadCrumb } = state;

    return (
      <List sx={{ width: '100%' }} component="nav" aria-labelledby="nav-sections-and-articles" >
        {articleSectionsExternalBreadCrumb.map(renderLabelSectionsAndArticles)}
      </List>
    )
  }

  function renderArticleInformation() {
    const title = article.title || "";
    const subtitle = article.subtitle || "";
    const readingtime = article.readingtime || "";

    const createdAt = article.createdat ? moment.utc(article.createdat).format('DD/MM/YYYY HH:mm') : "";
    const updatedAt = article.updatedat ? moment.utc(article.updatedat).format('DD/MM/YYYY HH:mm') : "";

    const hasCreateUserData = article.createduserdata && article.createduserdata.id !== 0;
    const createdUserData = hasCreateUserData ? article.createduserdata : {};
    const createdUserName = createdUserData.name || "";

    const hasUpdateUserData = article.updateduserdata && article.updateduserdata.id !== 0;
    const updatedUserData = hasUpdateUserData ? article.updateduserdata : {};
    const updatedUserName = updatedUserData.name || "";


    return (
      <Stack
        flexDirection={"column"}
        alignItems={"flex-start"}
        justifyContent={"center"}
        gap={1}
        mb={1}
        width={"100%"}
      >
        <Typography
          variant="h2"
          paddingBottom={0}
          sx={{
            width: '100%',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="h4"
            sx={{
              marginTop: '0 !important',
              width: '100%',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
            maxWidth={"100%"}
          >
            {subtitle}
          </Typography>
        )}

        <Stack gap={0.3}>
          {readingtime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: subtitle ? 0 : 0.5 }}>
              <CustomIcon icon={AccessTime} color="text.secondary" iconSx={{ fontSize: 14 }} />

              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500, lineHeight: 'normal' }}
              >
                {readingtime}
              </Typography>
            </Box>
          )}

          {(createdAt || createdUserName) && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {`Criado por ${createdUserName} em ${createdAt}`}
            </Typography>
          )}

          {(updatedAt || updatedUserName) && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {`Atualizado por ${updatedUserName} em ${updatedAt}`}
            </Typography>
          )}
        </Stack>
      </Stack>
    )

  }

  useEffect(() => {
    const scrollElement = topRef?.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const scrollHeight = scrollElement.scrollHeight;
      const clientHeight = scrollElement.clientHeight;
      const scrollableHeight = scrollHeight - clientHeight;
      const scrollTop = scrollElement.scrollTop;
      const scrolledPastHalf = scrollTop > (scrollableHeight * 0.3);
      setShowScrollTop(scrolledPastHalf);
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [topRef]);

  if (!state.article || state.article?.id === 0) {
    return (
      <Grid
        container
        marginX={"20vw"}
        mt={3}

        maxWidth={'60vw'}
        justifyContent={"center"}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '500px',
            margin: '40px auto',
          }}
        >
          <CustomIcon icon={Search} color={defaultClientColor} iconSx={{ fontSize: 64, marginBottom: '16px' }} />
          <Typography
            variant="h5"
            sx={{
              color: '#374151',
              fontWeight: 600,
              marginBottom: '8px'
            }}
          >
            <FormattedMessage id="article.content.not.found" />
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#6b7280',
              marginBottom: '24px',
              maxWidth: '350px'
            }}
          >
            <FormattedMessage id="article.not.found.description" />
          </Typography>
        </Box>
      </Grid>
    )
  }

  return (
    <React.Fragment>
      <Grid
        container
        ml={"22vw"}
        mr={"12vw"}
        mt={3}
        maxWidth={'100vw'}
        alignItems={"flex-start"}
        justifyContent={"space-between"}
        gap={0.5}
      >
        {hasArticleSectionsExternalBreadCrumb &&
          <Grid item container sm={2.8} width={"100%"}>
            {renderSectionsAndArticlesBreadCrumb()}
          </Grid>
        }

        <Grid item container sm={9} gap={1} width={"100%"}>
          <Grid item sm={12} display="flex" flexDirection="row" >
            {renderSectionsBreadCrumb()}
          </Grid>

          <Grid item sm={12} width={"100%"}>
            {renderArticleInformation()}

            <Divider />
          </Grid>

          <Grid item sm={12} >
            <div ref={contentRef} dangerouslySetInnerHTML={{ __html: article.content }} />

            <ArticleFeedback
              state={state}
              handleInitialFeedback={handleInitialFeedback}
              resetFeedbackState={resetFeedbackState}
              handleChange={handleChange}
              handleFeedbackSubmit={handleFeedbackSubmit}
            />
          </Grid>

          <Grid item sm={12} >
            {article.articlesattachments.length > 0 && (
              <Grid item xs={12} style={{ marginTop: 15 }}>
                <div className={classes.attachmentsBox}>
                  <label>{<FormattedMessage id="attachments" />}</label>
                  <ImageList
                    cellHeight={100}
                    className={classes2.ImageList}
                    cols={1}
                  >
                    <ul>
                      {article.articlesattachments.map((file) => {
                        return (
                          <li
                            key={file.attachmentid}
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <CustomIcon icon={AttachFile} color={defaultClientColor} />
                            <Button onClick={() => downloadFile(file)}
                              sx={{
                                color: `${defaultClientColor} !important`,
                                "&:hover": {
                                  backgroundColor: `${alpha(defaultClientColor, 0.1)} !important`,
                                }
                              }}
                            >
                              {file.name}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </ImageList>
                </div>
              </Grid>
            )}
          </Grid>

          {state.enableTagsOnArticles &&
            <Grid item xs={12} sm={12} mb={2} >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6b7280",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: 1,
                    display: "block",
                  }}
                >
                  <FormattedMessage id="menu.tags" />
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {state.tags.map((tag, index) => (
                    <FilterStyledChip
                      key={index}
                      label={tag}
                      onClick={() => handleTagClick(tag)}
                      color="primary"
                      variant="outlined"
                      customcolor={defaultClientColor}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          }
        </Grid>
      </Grid>

      {/* Lightbox Modal para ampliar imagens */}
      <LightBoxModal
        contentRef={contentRef}
        tooltip={intl.formatMessage({ id: "click.to.enlarge" })}
        imageErrorText={intl.formatMessage({ id: "image.unavailable" })}
        dependency={article?.content}
      />

      {/* Botão flutuante para voltar ao topo */}
      <Zoom in={showScrollTop} unmountOnExit>
        <Fab
          size="medium"
          onClick={goToScrollTop}
          sx={{
            position: 'fixed',
            bottom: 28,
            right: 52,
            zIndex: 1000,
            backgroundColor: clientportal.defaultcolor ? `${clientportal.defaultcolor} !important` : undefined,
            '&:hover': {
              filter: 'brightness(0.9)',
            },
          }}
          aria-label="scroll to top"
        >
          <CustomIcon icon={KeyboardArrowUp} color="#fff" />
        </Fab>
      </Zoom>
    </React.Fragment>
  )
}